import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';

export const Signup: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate inputs
            if (!email || !password) {
                throw new Error('Please fill in all fields');
            }

            if (password.length < 6) {
                throw new Error('Password must be at least 6 characters');
            }

            // Retrieve jobId from sessionStorage if it exists
            const pendingJobId = sessionStorage.getItem('pendingJobId');

            // Store credentials and jobId temporarily for account creation after onboarding
            const signupData: any = { email, password };
            if (pendingJobId) {
                signupData.jobId = pendingJobId;
                console.log('[Signup] Preserving jobId through signup flow:', pendingJobId);
            }

            sessionStorage.setItem('pendingSignup', JSON.stringify(signupData));

            // Navigate to onboarding (account will be created after completion)
            navigate('/onboarding');
        } catch (err: any) {
            setError(err.message || 'Failed to proceed to onboarding');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 py-8">
            {/* Back Button */}
            <div className="w-full max-w-md mb-3 md:mb-4">
                <button
                    onClick={() => navigate('/signup-selection')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
                >
                    <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm md:text-base font-medium">Back</span>
                </button>
            </div>

            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-6 md:mb-8">
                    <div className="flex items-center justify-center space-x-2 md:space-x-3 mb-3 md:mb-4">
                        <img src="/JobVitalLogo.png" alt="JobVital" className="h-8 md:h-12 w-auto" />
                        <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                            JobVital
                        </span>
                    </div>
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Job Seeker Sign Up</h2>
                    <p className="text-xs md:text-sm text-slate-500 mt-1 md:mt-2">Create your profile and find your next opportunity</p>
                </div>

                {/* Card */}
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 p-6 md:p-8">
                    <form onSubmit={handleSignup} className="space-y-4 md:space-y-6">
                        {error && (
                            <div className="p-3 md:p-4 bg-red-50 border border-red-100 text-red-600 text-xs md:text-sm rounded-xl flex items-start">
                                <div className="flex-shrink-0 w-1 h-1 mt-2 rounded-full bg-red-600 mr-2" />
                                {error}
                            </div>
                        )}

                        <div className="space-y-1.5 md:space-y-2">
                            <label className="text-xs md:text-sm font-medium text-gray-700 ml-1">Email</label>
                            <div className="relative group">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-600 outline-none transition-all text-sm md:text-base"
                                    placeholder="john@example.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5 md:space-y-2">
                            <label className="text-xs md:text-sm font-medium text-gray-700 ml-1">Password</label>
                            <div className="relative group">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-600 outline-none transition-all text-sm md:text-base"
                                    placeholder="Create a password"
                                    minLength={6}
                                    required
                                />
                            </div>
                            <p className="text-[10px] md:text-xs text-gray-500 ml-1">Must be at least 6 characters</p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-2.5 md:py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm md:text-base font-semibold rounded-xl shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                            ) : (
                                <>
                                    Continue to Sign Up
                                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-100 text-center">
                        <p className="text-xs md:text-sm text-gray-600">
                            Already have an account?{' '}
                            <Link to="/login-selection" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
